import { useMemo } from 'react';
import { allProPlansChildren, allProPlansIds, highStakes, highStakesAdditional, seams } from '../constants';
import { IPlan, ISubscription } from '../model';

export const useProcessPlans = (plans: IPlan[], subscriptions: ISubscription[]): {
  plans: IPlan[];
  totalPrice: number;
  currentSubscriptions: Record<string, number>
} => {
  // collect all plans with their subscriptions
  const subscribedPlans = useMemo(() => {
    return subscriptions.map(subscription => subscription.plans.map(plan => {
      return {
        ...plan,
        subscription
      }
    })).flat(1);
  }, [subscriptions]);

  return useMemo(() => {
    const currentSubscriptions: Record<string, number> = {};

    // get the current highest subscription for each sport category and get their ids
    const subscribedPlanIds = subscribedPlans.map(plan => plan.plan_id);
    plans.forEach((plan) => {
      if (subscribedPlanIds.includes(plan.plan_id)) {
        if (currentSubscriptions[plan.display.sport_category] && currentSubscriptions[plan.display.sport_category] > plan.plan_tier) {
          return
        }
        currentSubscriptions[plan.display.sport_category] = plan.plan_tier
      }
    });

    // Disable plans based on current subscriptions
    // If the plan tier is less than or equal to the current subscription tier, disable it
    const processedPlans = plans.map(plan => {
      if (
        ((plan.display.sport_category === 'all' || plan.display.sport_category === 'Stats')
          ? (plan.plan_tier < Math.max(currentSubscriptions['all'] || 0, currentSubscriptions['Stats'] || 0))
          : (plan.plan_tier <= Math.max(currentSubscriptions['all'] || 0, currentSubscriptions['Stats'] || 0))) ||
        (plan.plan_tier < currentSubscriptions[plan.display.sport_category] || 0)) plan.disabled = true;
      return plan;
    })

    const highStakesAdditionalPlan = plans.find(plan => highStakesAdditional.includes(plan.plan_id));
    const allProPlan = plans.find(plan => allProPlansIds.includes(plan.plan_id));
    const seamsPlans = plans.filter(plan => seams.includes(plan.plan_id))

    let totalPrice = 0;
    const resPlans = processedPlans.filter(plan =>
      subscribedPlanIds.includes(plan.plan_id) ? true :
        !highStakesAdditional.includes(plan.plan_id) &&
        !allProPlansIds.includes(plan.plan_id)
    ).map(plan => {
      const additional = allProPlansChildren.includes(plan.plan_id) ? allProPlan : highStakes.includes(plan.plan_id) ? highStakesAdditionalPlan : undefined;
      const additionalSeams = seamsPlans.find(seamsPlan => seamsPlan.encapsulated_plans.includes(plan.plan_id));

      const foundPlan = subscribedPlans.find(findPlan => findPlan.plan_id === plan.plan_id);
      if (foundPlan) {
        totalPrice += Number(foundPlan.interval === 'year' ? Number(foundPlan.price) / 12 : foundPlan.price) / 100;
        plan.current = foundPlan;
      }
      if (additional) {
        plan.additional = additional;
        const foundAdditionalPlanCurrent = subscribedPlans.find(findPlan => findPlan.plan_id === additional?.plan_id);
        if (foundAdditionalPlanCurrent) {
          plan.additional.current = foundAdditionalPlanCurrent;
        }
      }
      if (additionalSeams) {
        plan.additional = additionalSeams;
        const foundAdditionalPlanCurrent = subscribedPlans.find(plan => plan.plan_id === additionalSeams?.plan_id);
        if (foundAdditionalPlanCurrent) {
          plan.additional.current = foundAdditionalPlanCurrent;
        }
      }
      return plan;
    }).sort((plan1, plan2) => {
      const diff = plan1.plan_tier - plan2.plan_tier;
      if (diff === 0) {
        return plan1.plan_id > plan2.plan_id ? 1 : -1;
      }
      return diff;
    });

    return {
      plans: resPlans,
      totalPrice,
      currentSubscriptions
    }
  }, [plans, subscriptions, subscribedPlans]);
}
